import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Container, Row } from 'shards-react';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardActions } from '../actions';
import CheckPermission from '../components/checkPermission/CheckPermission';

import PageTitle from '../components/common/PageTitle';
import UsersOverview from '../components/charts/UsersOverview';
import PaymentOverview from '../components/charts/PaymentOverview';
import MonthlyUsers from '../components/charts/MonthlyUsers';
import SubscriptionByPlan from '../components/charts/SubscriptionByPlan';
import RetentionOverview from '../components/charts/RetentionOverview';

const buildDashboardQuery = (range) => {
  const now = new Date();
  const to = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
  ));
  let from;
  let granularity = 'day';

  if (range === 'week') {
    from = new Date(to.getTime() - (7 * 24 * 60 * 60 * 1000));
  } else if (range === 'year') {
    from = new Date(Date.UTC(
      to.getUTCFullYear() - 1,
      to.getUTCMonth() + 1,
      1,
    ));
    granularity = 'month';
  } else if (range === 'all') {
    granularity = 'month';
  } else {
    from = new Date(to.getTime() - (30 * 24 * 60 * 60 * 1000));
  }

  return {
    ...(from ? { from: from.toISOString() } : {}),
    to: to.toISOString(),
    timezone: 'UTC',
    granularity,
    range,
  };
};

const StateCard = ({
  className,
  hoverTitle,
  label,
  loading,
  value,
}) => (
  <div className="stateCard" title={hoverTitle}>
    <span className={`stats-small__label text-uppercase mb-1 ${className || ''}`}>
      {label}
    </span>
    <h6>{loading ? '…' : value}</h6>
  </div>
);

const RequestState = ({
  error,
  loading,
  onRetry,
  title,
}) => {
  if (loading) {
    return <div className="text-muted py-2">{title} is loading…</div>;
  }

  if (!error) return null;

  return (
    <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
      <span>{error}</span>
      <Button size="sm" theme="danger" outline onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
};

StateCard.propTypes = {
  className: PropTypes.string,
  hoverTitle: PropTypes.string,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

StateCard.defaultProps = {
  className: '',
  hoverTitle: '',
  loading: false,
};

RequestState.propTypes = {
  error: PropTypes.string,
  loading: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

RequestState.defaultProps = {
  error: null,
  loading: false,
};

const formatNumber = value => Number(value || 0).toLocaleString('en-US');

const Dashboard = () => {
  const dispatch = useDispatch();
  const [selectedRange, setSelectedRange] = useState('month');
  const summaryState = useSelector(state => state.dashboard.summary);
  const chartsState = useSelector(state => state.dashboard.charts);
  const query = useMemo(
    () => buildDashboardQuery(selectedRange),
    [selectedRange],
  );

  const summary = summaryState.data || {};
  const charts = chartsState.data || {};
  const paymentTotals = summary.paymentTotalsByCurrency || {};

  useEffect(() => {
    dispatch(dashboardActions.getSummary());
  }, [dispatch]);

  useEffect(() => {
    dispatch(dashboardActions.getCharts(query));
  }, [dispatch, query]);

  const retrySummary = () => dispatch(dashboardActions.getSummary());
  const retryCharts = () => dispatch(dashboardActions.getCharts(query));
  const summaryLoading = summaryState.status === 'loading' && !summaryState.data;
  const chartsLoading = chartsState.status === 'loading' && !chartsState.data;
  const chartsRefreshing = chartsState.status === 'loading';
  const generatedAt = (charts.meta && charts.meta.generatedAt) || summary.generatedAt;

  return (
    <Container fluid className="main-content-container px-4">
      <Row noGutters className="page-header py-4">
        <PageTitle title="Overview" subtitle="Dashboard" className="text-sm-left mb-3" />
      </Row>

      <RequestState
        title="Dashboard summary"
        loading={summaryLoading}
        error={summaryState.error}
        onRetry={retrySummary}
      />

      <Row>
        <CheckPermission resource="user" action={['statistics']}>
          <Col>
            <StateCard
              label="Users"
              loading={summaryLoading}
              value={formatNumber(summary.usersCount)}
            />
          </Col>
        </CheckPermission>
        <CheckPermission resource="plan" action={['statistics']}>
          <Col>
            <StateCard
              label="Enabled Plans"
              loading={summaryLoading}
              value={formatNumber(summary.activePlansCount)}
            />
          </Col>
        </CheckPermission>
        <CheckPermission resource="payment" action={['statistics']}>
          <Col>
            <StateCard
              label="Payments"
              loading={summaryLoading}
              value={formatNumber(summary.paymentsCount)}
              hoverTitle={`${formatNumber(paymentTotals.IRR)} IRR | ${formatNumber(paymentTotals.EUR)} EUR`}
            />
          </Col>
        </CheckPermission>
        <CheckPermission resource="design" action={['statistics']}>
          <Col>
            <StateCard
              label="Designs"
              loading={summaryLoading}
              value={formatNumber(summary.totalDesigns)}
            />
          </Col>
        </CheckPermission>
        <CheckPermission resource="template" action={['statistics']}>
          <Col>
            <StateCard
              label="Templates"
              loading={summaryLoading}
              value={formatNumber(summary.templatesCount)}
            />
          </Col>
        </CheckPermission>
      </Row>

      <RequestState
        title="Dashboard charts"
        loading={chartsLoading}
        error={chartsState.error}
        onRetry={retryCharts}
      />

      <CheckPermission resource="user" action={['statistics']}>
        <React.Fragment>
          <Row>
            <Col lg="8" md="12" sm="12" className="mb-4">
              <UsersOverview
                loading={chartsRefreshing}
                onRangeChange={setSelectedRange}
                range={selectedRange}
                users={(charts.users && (
                  selectedRange === 'week' || selectedRange === 'month'
                    ? charts.users.daily
                    : charts.users.monthly
                )) || []}
              />
            </Col>
            <Col lg="4" md="6" sm="12" className="mb-4">
              <MonthlyUsers users={(charts.users && charts.users.monthly) || []} />
            </Col>
            <Col lg="5" md="6" sm="12" className="mb-4">
              <SubscriptionByPlan
                subscriptions={summary.subscriptionsByPlanStatus || []}
              />
            </Col>
            <Col lg="7" md="12" sm="12" className="mb-4">
              <PaymentOverview
                payments={(charts.payments && charts.payments.byPeriodAndCurrency) || []}
                meta={(charts.payments && charts.payments.meta) || {}}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="8" md="12" sm="12" className="mb-4">
              <RetentionOverview retention={charts.retention || []} />
            </Col>
          </Row>
        </React.Fragment>
      </CheckPermission>

      {generatedAt && (
        <div className="text-muted small pb-3">
          Last updated: {new Date(generatedAt).toLocaleString()}
        </div>
      )}
    </Container>
  );
};

export default Dashboard;
